import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { DriverActivityComponent } from '../../driver-activity/driver-activity.component';
import { DriverOperationsService, FilterService } from '../../../../../_shared/services';
import { endOfDay, startOfDay } from 'date-fns';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { PaginationRequest } from '../../../../../_shared/models/pagination-request.model';
import { OperationStatus } from '../../../models/operation-status.model';
import { debounceTime, Observable, Subject, Subscription } from 'rxjs';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-stationary-drivers-modal',
  standalone: true,
  imports: [DriverActivityComponent, TranslocoPipe],
  templateUrl: './stationary-drivers-modal.component.html',
  styleUrl: './stationary-drivers-modal.component.scss',
})
export class StationaryDriversModalComponent implements OnInit, OnDestroy {
  @Input() isModal: boolean = false;
  @Input() componentId: string = '';
  @Output() toggleExpandEvent = new EventEmitter<string>();
  @Input() search$: Observable<string>;

  private searchTerm: string = '';
  private filterSubscription: Subscription;
  private hasMoreData: boolean = true;
  private isExpanded: boolean = false;
  public title: string = '';
  public count: number = 0;
  public columnHeaders: string[] = [];
  public driversData: OperationStatus[] = [];
  public filteredDriversData: OperationStatus[] = [];
  public isLoading: boolean = true;
  public isFetching: boolean = false;
  public hasError: boolean = false;
  public currentDate: Date = new Date();
  public paginationRequest: PaginationRequest = {
    accountIds: [],
    timeFrom: startOfDay(this.currentDate),
    timeTo: endOfDay(this.currentDate),
    page: 1,
    pageSize: 20,
    searchTerm: ''
  };

  private driverOperationsService = inject(DriverOperationsService);
  private filterService = inject(FilterService);
  private errorHandlerService = inject(ErrorHandlerService);
  private transloco = inject(TranslocoService);
  private cancelSubject = new Subject<void>();
  private searchSubscription: Subscription | null = null;

  ngOnInit(): void {
    this.initializeDriversValues();

    this.filterSubscription = this.filterService.filterUpdated$.subscribe(filter => {
      if (!filter || Object.values(filter).every(x => x == null)) {
        return;
      }

      this.driversData = [];
      this.paginationRequest.page = 1;
      this.hasMoreData = true;
      this.hasError = false;

      this.paginationRequest.accountIds = filter.accountIds;
      this.onFilterChangeAsync();
    });

    if (this.search$) {
      this.searchSubscription = this.search$.pipe(debounceTime(500)).subscribe(searchTerm => {
        this.isFetching = false;
        this.searchTerm = searchTerm;
        this.isLoading = true;
        this.hasError = false;
        this.filterDriversAsync(searchTerm);
      });
    }
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
    if (this.filterSubscription) {
      this.filterSubscription.unsubscribe();
    }

    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  private initializeDriversValues(): void {
    this.columnHeaders = [this.transloco.translate('driverDashboard.table.driver'), this.transloco.translate('driverDashboard.table.vehicle'), this.transloco.translate('driverDashboard.columns.continuousStationaryTime'), this.transloco.translate('driverDashboard.columns.totalStationaryTime')];
  }

  private async getStationaryDriversAsync() {
    if (this.isFetching || !this.hasMoreData) {
      return;
    }

    this.isLoading = true;
    this.isFetching = true;
    this.hasError = false;
    try {
      this.cancelSubject.next();
      const response = await this.driverOperationsService.getStationaryDrivers(this.paginationRequest, this.cancelSubject);
      this.driversData = [...this.driversData, ...response.data?.results ?? []];
      this.filteredDriversData = this.driversData;
      this.count = response.data?.totalCount ?? 0;
      this.hasMoreData = this.driversData.length < response.data?.totalCount!;

      this.isLoading = false;
      this.isFetching = false;
    } catch (error: any) {
      this.hasError = true;
      this.errorHandlerService.handleError(error, {
        showToast: true,
        callback: () => {
          this.isLoading = false;
          this.isFetching = false;
        }
      });
    }
  }

  async onExpand(event: any): Promise<void> {
    this.isExpanded = !this.isExpanded;
    this.hasMoreData = true;
    this.isLoading = true;
    this.hasError = false;
    this.driversData = [];
    this.paginationRequest.page = 1;
    this.isFetching = false;
    this.paginationRequest.pageSize = this.isExpanded ? 20 : 5;
    this.paginationRequest.searchTerm = '';
    this.cancelSubject.next();
    this.toggleExpandEvent.emit(event);
  }

  async onFilterChangeAsync(): Promise<void> {
    await this.getStationaryDriversAsync();
  }

  async onScroll(): Promise<void> {
    if (this.hasMoreData && !this.isFetching) {
      this.paginationRequest.page += 1;
      await this.getStationaryDriversAsync();
    }
  }

  private async filterDriversAsync(searchTerm: string): Promise<void> {
    this.paginationRequest.searchTerm = searchTerm ?? '';
    this.driversData = [];
    this.paginationRequest.page = 1;
    const previousCount = this.count;
    this.hasMoreData = true;
    await this.getStationaryDriversAsync()
    this.count = previousCount; //Displaying total count regardless the search filter.
    const searchLower = this.normalizeSearchTerm(this.searchTerm);
    const tokens = searchLower.split(' ').filter(Boolean);
    this.filteredDriversData = this.driversData.filter(driver => {
      const fullName = this.normalizeSearchTerm(`${driver.firstName} ${driver.lastName}`);
      return tokens.every(t => fullName.includes(t));
    });
  }

  private normalizeSearchTerm(searchTerm: string): string {
    return searchTerm.toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }
}